<template>
  <div v-if="bill" class="repayment-wrapper">
    <div v-if="!isConfirming" class="repayment-input">
      <CommonBackdrop v-model:isOpen="isBackdropVisible">
        <template #title>
          <div class="backdrop-title" v-html="backdropTitleText"></div>
        </template>
        <div class="backdrop-content">
          <Details
            :data="bill"
            :simpleId="simpleId"
            :historyItems="historyItems"
          />
        </div>
      </CommonBackdrop>
      <div class="monthly-payment" @click="showBackdrop">
        <ListItem
          :title="$t`За покупки`"
          :tailText="$currencyFormatter(bill.shops_list_sum)"
        />
        <div class="icon-container">
          <div class="i-arrow"></div>
        </div>
      </div>
      <template v-for="fee in fees" :key="fee.title">
        <ListItem
          v-if="fee.amount"
          class="repayment-input__fee"
          :title="fee.title"
          :tailText="$currencyFormatter(fee.amount)"
        />
      </template>

      <div class="gap-30"></div>

      <ListItem
        :title="$t`Оплачено ранее`"
        :tailText="$currencyFormatter(bill.payments_list_sum)"
      />
      <ListItem>
        <template #title>
          <span class="font-medium">{{ $t`Итого к оплате` }}</span>
        </template>
        <template #tail>
          <div class="font-medium">
            {{ $currencyFormatter(bill.left_to_pay_total_sum) }}
          </div>
        </template>
      </ListItem>

      <div class="gap-30"></div>

      <ListItem
        v-if="paymentSources.length"
        icon="gold"
        class="payment-src"
        :title="paymentSources[0].title"
        :tailText="$currencyFormatter(paymentSources[0].amount, null, 2)"
      />

      <template v-if="paymentSources.length > 0">
        <div class="comission text-grey text-size-s">
          {{ $t`Комиссия` }} 0
          <span class="tenge">₸</span>
        </div>
        <CommonButton spaced @click="confirm">
          {{
            `${$gettext("Оплатить")} ${$currencyFormatter(
              bill.left_to_pay_total_sum
            )}`
          }}
        </CommonButton>
      </template>
      <PaymentTerminal v-else />
    </div>
    <div v-else class="repayment-confirm">
      <div class="repayment-confirm__info">
        <div class="repayment-confirm__info_title">
          {{ $t`Вы оплачиваете:` }}
        </div>
        <TwoColumnTable :data="tableData" />
      </div>
      <ListItem :tailText="$currencyFormatter(bill.left_to_pay_total_sum)">
        <template #title>
          <span class="font-medium">{{ $t`Итого к оплате` }}</span>
        </template>
      </ListItem>
      <ListItem
        v-if="paymentSources.length"
        icon="gold"
        :title="paymentSources[0].title"
        :tailText="$currencyFormatter(paymentSources[0].amount, null, 2)"
      />
      <CommonButton spaced @click="payBill">
        {{
          `${$gettext("Подтвердить и оплатить")} ${$currencyFormatter(
            bill.left_to_pay_total_sum
          )}`
        }}
      </CommonButton>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { computed, onBeforeMount, shallowRef } from "@vue/runtime-core";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { getFormattedDate } from "@/utils/date";
import { gettext, l10nformat, t } from "@/plugins/l10n";
import { numberWithCurrency } from "@/utils/numberCurrency";
import { fetchBills } from "@/api/bills";
import { fetchInvoices } from "@/api/calendar";
import { fetchRepaymentAccounts } from "@/api/accounts";
import { redRepayment } from "@/api/payments";
import duplicatePreventor from "@/api/decorators/duplicatePreventor";
import CommonButton from "@/components/shared/button/CommonButton.vue";
import ListItem from "@/components/shared/button/ProductButton.vue";
import CommonBackdrop from "@/components/shared/backdrop/CommonBackdrop.vue";
import PaymentTerminal from "@/components/payments/PaymentTerminal.vue";
import Details from "@/components/bills-calendar/RepaymentItemDetails.vue";
import TwoColumnTable from "@/components/shared/table/TwoColumnTable.vue";
import { logEvent } from "@/utils/analytics";

export default {
  name: "RedRepayment",
  components: {
    CommonBackdrop,
    Details,
    ListItem,
    CommonButton,
    PaymentTerminal,
    TwoColumnTable,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const paymentSources = shallowRef([]);
    const bill = shallowRef(null);
    const historyItems = shallowRef([]);
    const isBackdropVisible = shallowRef(false);
    const isConfirming = shallowRef(false);
    const simpleId = shallowRef();
    const commitPayment = duplicatePreventor(redRepayment);

    const fees = computed(() => [
      {
        title: t`Клубный взнос`,
        amount: bill.value.club_fee,
      },
      {
        title: t`Комиссия за перевыпуск`,
        amount: bill.value.emb_fee,
      },
      {
        title: t`Штраф`,
        amount: bill.value.penalty_list_sum,
      },
    ]);

    const tableData = computed(() => [
      {
        key: t`Счет`,
        value: `Kaspi Red ${l10nformat(t`до #DATE#`, {
          DATE: getFormattedDate(bill.value.pay_until_date, "D MMMM"),
        })}`,
      },
      {
        key: t`За покупки`,
        value: numberWithCurrency(bill.value.shops_list_sum),
      },
      {
        key: t`Итого по счету`,
        value: numberWithCurrency(bill.value.left_to_pay_total_sum),
      },
      {
        key: t`Внесено ранее`,
        value: numberWithCurrency(bill.value.payments_list_sum),
      },
    ]);

    const backdropTitleText = computed(() => {
      const planDate = dayjs(bill.value.pay_until_date, "YYYY-MM-DD");
      const pattern =
        bill.value.status === "OVERDUE"
          ? "Просроченный счет за #DATE#"
          : "Счет за #DATE#";
      return (
        l10nformat(gettext(pattern), {
          DATE: planDate.format("MMMM"),
        }) + ` ${numberWithCurrency(bill.value.left_to_pay_total_sum)}`
      );
    });

    async function getBills() {
      simpleId.value = store.getters["red/redModel"]?.simple_id;
      try {
        if (typeof simpleId.value === "undefined") {
          const bundle = await store.dispatch("red/getRedModel");
          simpleId.value = bundle.simple_id;
        }

        const [bills, sources] = await Promise.all([
          fetchBills({
            redSimpleId: simpleId.value,
            shouldFetchPaidBills: false,
          }),
          fetchRepaymentAccounts(),
        ]);

        if (!bills || bills.length === 0 || bills[0].status === "PAID") {
          router.back();
          return;
        }

        bill.value = bills[0];
        paymentSources.value = sources || [];
      } catch {
        router.back();
      }
    }

    async function getHistoryItems() {
      const { shops_list } = await fetchInvoices({
        simpleId: simpleId.value,
        billNum: bill.value.invoice_num,
        date: null,
        type: "FromPayment",
      });
      return shops_list;
    }

    async function showBackdrop() {
      historyItems.value = await getHistoryItems();
      isBackdropVisible.value = true;
    }

    function confirm() {
      if (bill.value.left_to_pay_total_sum > paymentSources.value[0].amount) {
        store.dispatch("system/showAlert", {
          title: t`У вас недостаточно денег`,
          text: t`Пополните Kaspi Gold`,
        });
        return;
      }
      isConfirming.value = true;
      logEvent("mybank_red_bill_repayment", {
        step: "confirmation",
        page_path: "",
        started_from: "",
      });
    }

    async function payBill() {
      await commitPayment(bill.value.left_to_pay_total_sum);
      router.replace({
        path: "/bank/red/redRepayment/success",
        query: {
          amount: bill.value.left_to_pay_total_sum,
          date: bill.pay_until_date,
        },
      });
    }

    onBeforeMount(() => {
      logEvent("mybank_red_bill_repayment", {
        step: "info_page",
        page_path: "",
        started_from: "",
      });
      getBills();
    });

    return {
      fees,
      bill,
      payBill,
      confirm,
      simpleId,
      tableData,
      isConfirming,
      showBackdrop,
      historyItems,
      paymentSources,
      backdropTitleText,
      isBackdropVisible,
    };
  },
};
</script>

<style lang="scss" scoped>
.repayment-input {
  padding-top: 10px;

  &__fee {
    margin-top: 1px;
  }
}

.icon-container {
  flex: 1;
  min-width: 30px;
  padding-right: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.product-btn {
  margin-bottom: 1px;
}

.comission {
  padding: 20px 0 5px;
}

.gap-30 {
  height: 30px;
}

.monthly-payment {
  width: 100vw;
  display: flex;
  align-items: center;
  background: white;

  .product-btn {
    width: calc(100vw - 20px);
  }
}

.repayment-confirm {
  padding-top: 10px;

  &__info {
    margin-bottom: 20px;
    background-color: #f9f9f9;
    padding: 20px 15px 15px;

    &_title {
      text-align: left;
      margin-bottom: 10px;
    }
  }
}
.backdrop-title::first-letter {
  text-transform: capitalize;
}
</style>
